import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AxiosResponse } from "axios";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router";

import { Filter } from "components/Filter";
import { Loading } from "components/loading";
import { showModal } from "components/popup";
import { showToast } from "components/toast";
import { BaseButton, InfoArea } from "iebt-zing-storybook";
import {
  GetFormat,
  getProvider,
  PostFormat,
  postProvider,
  useGet,
} from "sharedKernel/api";
import {
  toBase64,
  triggerFileDownloadOnClientSide,
} from "sharedKernel/file/handler";
import { trans } from "sharedKernel/i18n";
import { FilterOrderBar } from "sharedKernel/styledComponents/filterComponents/style";
import {
  changeEvaluatorSolutions,
  resetEvaluatorSolutions,
  store,
  TReducers,
  useSelector,
} from "store";
import { AssignModal } from "./AssignModal";
import { ButtonArea } from "./styles";
import { HubCompanyEvaluatorsAssignTable } from "./table";
import { sortSolutions } from "./table/behavior";

interface TablesData {
  header: string[];
  rows: string[][];
}

export const HubCompanyEvaluatorsAssign = (): JSX.Element => {
  const labels = {
    name: trans("Atribuir avaliador"),
    infoMsg: trans(
      "Baixe o arquivo modelo abaixo para preenchimento dos avaliadores convidados e em seguida importe-os no botão abaixo."
    ),
    importTitle: trans("Importar"),
    exportTitle: trans("Exportar"),
    onImportSuccess: trans("Avaliadores importados com sucesso"),
    onImportSomeError: trans("Alguns avaliadores não puderam ser importados"),
    status: trans("Status"),
    solution: trans("Solução"),
    assignEvaluatorSuccess: trans("Atribuições salvas com sucesso"),
    assignEvaluatorFail: trans("Houve um erro ao atribuir avaliador"),
    editAssign: trans("Editar Atribuição"),
    confirmMessage: trans(
      "Deseja realmente alterar as atribuições dos avaliadores selecionados?"
    ),
    modalButton: trans("Salvar"),
    modalCancelButton: trans("Cancelar"),
  };

  const evaluatorAssignedSolutions = useSelector(
    (state: TReducers) => state.evaluatorSolutionState
  );

  const downloadFileRef: React.RefObject<HTMLAnchorElement> = React.createRef();
  const uploadFileRef: React.RefObject<HTMLInputElement> = React.createRef();

  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [selectedStep, setSelectedStep] = useState<string>("");
  const [filteredEvaluatorsData, setFilteredEvaluatorsData] =
    useState<any>(null);
  const { challengeId } = useParams();
  const [tablesData, setTablesData] = useState<TablesData>({
    header: [],
    rows: [],
  });

  const requestEvaluators: GetFormat = {
    url: `/v1/hub/company/challenge/${challengeId}/evaluators?page=1`,
  };

  const requestSteps: GetFormat = {
    url: `/v1/hub/company/challenge/${challengeId}/steps`,
  };

  const { data: stepsData, isFetching: isStepsFetching } = useGet<any>(
    requestSteps,
    "steps-get"
  );

  const {
    data: evaluatorData,
    isFetching: isEvaluatorFetching,
    refetch: evaluatorRefetch,
  } = useGet<any>(requestEvaluators, "evaluator-get");

  const [solutionsData, setSolutionsData] = useState<any>({ rows: [] });
  const [isSolutionsFetching, setIsSolutionsFetching] = useState<boolean>(false);

  const { mutate: fetchStepEvaluators } = getProvider();
  const { mutate: fetchStepSolutions } = getProvider();

  useEffect(() => {
    setTimeout(() =>{
      if (!selectedStep){
        setSelectedStep(stepsData.data.steps[0].id);
        return;
      }

      const stepEvaluatorsRequest: GetFormat = {
        url: `/v1/hub/company/challenge/steps/${selectedStep}/evaluators?sort=string&limit=648&page=1&totalRows=740`,
      };

      const stepSolutionsRequest: GetFormat = {
        //retorna os status Created, Launched, Approved
        url: `/v1/hub/company/challenge/steps/${selectedStep}/solution?status=allActive`,
      };

      setIsSolutionsFetching(true);
      fetchStepSolutions(stepSolutionsRequest, {
        onSuccess: (data) => {
          const actualSolutionsData = data?.data || data;
          setSolutionsData(actualSolutionsData?.rows ? actualSolutionsData : { rows: actualSolutionsData || [] });
          setIsSolutionsFetching(false);
        },
        onError: (error) => {
          console.error("Error fetching step solutions:", error);
          setSolutionsData({ rows: [] });
          setIsSolutionsFetching(false);
        },
      });

      fetchStepEvaluators(stepEvaluatorsRequest, {
        onSuccess: (data) => {
          const actualData = data?.data || data;
          if (evaluatorData.rows) {
            evaluatorData.rows = actualData.rows;
          }
          setFilteredEvaluatorsData(actualData);
        },
        onError: (error) => {
          console.error("Error fetching step evaluators:", error);
          setFilteredEvaluatorsData(null);
        },
      });
    }, 100);
  }, [stepsData, selectedStep]);

  const onSuccessUploadAssingFile = (importedEvaluators: any): boolean => {
    let someError = false;
    const evaluatorsTableParams = importedEvaluators.map((evaluator) => {
      if (evaluator.status === "error") {
        someError = true;
      }
      return [
        evaluator.evaluatorName,
        evaluator.solutionTitle,
        evaluator.status,
      ];
    });
    const evaluatorsTableData = {
      header: [labels.name, labels.solution, labels.status],
      rows: [...evaluatorsTableParams],
    };
    setTablesData({ ...evaluatorsTableData });
    return someError;
  };

  const onSuccessUploadFile = async (
    response: AxiosResponse
  ): Promise<void> => {
    let someError = false;
    const importResponse = JSON.parse(response.request.response);

    someError = onSuccessUploadAssingFile(importResponse.rows);

    if (someError) {
      showToast("warning", labels.onImportSomeError);
    } else {
      showToast("success", labels.onImportSuccess);
    }
  };

  const { mutate: uploadFile } = postProvider(onSuccessUploadFile);
  const { mutate: downloadFile } = getProvider();

  const processFile = async (file: any): Promise<void> => {
    const fileContent = await toBase64(file);
    const mimetype = file.type;
    const fileData = {
      mimetype: mimetype,
      name: btoa(unescape(encodeURIComponent(file.name))),
      content: fileContent,
    };
    uploadFile({
      url: `/v1/hub/company/challenge/${challengeId}/evaluators-solutions/import`,
      data: { data: fileData },
    });
  };

  const { mutate: assignEvaluator } = postProvider();

const sucessfullyAssignEvaluator = (): void => {
    showToast("success", labels.assignEvaluatorSuccess);
    
    // 1. Atualiza a lista geral (se necessário)
    evaluatorRefetch(); 

    // 2. RECARREGA OS DADOS DA TABELA ATUAL (O Pulo do Gato 😼)
    // Usamos o selectedStep atual para buscar os dados frescos
    if (selectedStep) {
      const stepEvaluatorsRequest: GetFormat = {
        // Atenção: mantive os mesmos parâmetros que você usou no useEffect
        url: `/v1/hub/company/challenge/steps/${selectedStep}/evaluators?sort=string&limit=648&page=1&totalRows=740`,
      };

      // Chama o fetch novamente para atualizar a tabela
      fetchStepEvaluators(stepEvaluatorsRequest, {
        onSuccess: (data) => {
          const actualData = data?.data || data;
          // Atualiza o estado que alimenta a tabela
          setFilteredEvaluatorsData(actualData);
        },
        onError: (error) => {
          console.error("Error refreshing step evaluators:", error);
        },
      });

      // 3. Recarrega as soluções (lógica que você já tinha)
      const stepSolutionsRequest: GetFormat = {
        //retorna os status Created, Launched, Approved
        url: `/v1/hub/company/challenge/steps/${selectedStep}/solution?status=allActive`,
      };
      
      setIsSolutionsFetching(true);
      fetchStepSolutions(stepSolutionsRequest, {
        onSuccess: (data) => {
          const actualSolutionsData = data?.data || data;
          setSolutionsData(actualSolutionsData?.rows ? actualSolutionsData : { rows: actualSolutionsData || [] });
          setIsSolutionsFetching(false);
        },
        onError: (error) => {
          console.error("Error fetching step solutions:", error);
          setIsSolutionsFetching(false);
        },
      });
    }
  };

  const unsucessfullyAssignEvaluator = (): void => {
    showToast("error", labels.assignEvaluatorFail);
  };

  const showEditModal = (evaluator: any, solutionList: string[]): void => {
    // Check if selected step is "Conformidade especial - Sim ou Não"
    const selectedStepData = stepsData?.data?.steps?.find(
      (step: any) => step.id === selectedStep
    );
    const isSpecialCompliance =
      selectedStepData?.name === "Conformidade especial - Sim ou Não";
    // Get all assigned solutions from all evaluators (excluding current evaluator)
    let disabledSolutionIds: string[] = [];
    if (isSpecialCompliance && evaluatorData?.rows) {
      evaluatorData.rows.forEach((evaluatorRow: any) => {
        if (evaluatorRow.id !== evaluator.id) {
          evaluatorRow.resolvers?.forEach((resolver: any) => {
            resolver.solutions?.forEach((solution: any) => {
              disabledSolutionIds.push(solution.id);
            });
          });
        }
      });
    }

    showModal("assignModal", {
      title: labels.editAssign,
      popUpMessage: labels.confirmMessage,
      component: (
        <AssignModal
          evaluator={evaluator}
          evaluatorSolutions={solutionList}
          allSolutions={sortSolutions(solutionsData?.rows || [])}
          disabledSolutions={disabledSolutionIds}
          updateAssignedSolution={updateAssignedSolution}
          initializeAssignedSolutions={initializeAssignedSolutions}
          resetAssignedSolutions={resetAssignedSolutions}
        />
      ),
      buttons: [
        { text: labels.modalButton },
        { text: labels.modalCancelButton },
      ],
      action: () => {
        handleSaveAssignments(
          evaluator.id,
          evaluatorAssignedSolutions.evaluatorSolutions
        );
      },
    });
  };

  const updateAssignedSolution = (
    solutionId: string,
    isAssignSolution: boolean
  ): void => {
    if (isAssignSolution) {
      evaluatorAssignedSolutions.evaluatorSolutions = [
        ...evaluatorAssignedSolutions.evaluatorSolutions,
        solutionId,
      ];
    } else {
      evaluatorAssignedSolutions.evaluatorSolutions =
        evaluatorAssignedSolutions.evaluatorSolutions.filter((solution) => {
          return solution !== solutionId;
        });
    }

    store.dispatch(
      changeEvaluatorSolutions(evaluatorAssignedSolutions.evaluatorSolutions)
    );
  };

  const initializeAssignedSolutions = (assignedSolutions: string[]): void => {
    evaluatorAssignedSolutions.evaluatorSolutions = [
      ...evaluatorAssignedSolutions.evaluatorSolutions,
      ...assignedSolutions,
    ];
    store.dispatch(
      changeEvaluatorSolutions(evaluatorAssignedSolutions.evaluatorSolutions)
    );
  };

  const resetAssignedSolutions = (): void => {
    evaluatorAssignedSolutions.evaluatorSolutions = [];
    store.dispatch(
      resetEvaluatorSolutions(evaluatorAssignedSolutions.evaluatorSolutions)
    );
  };

  const handleSaveAssignments = (
    evaluatorId: string,
    assignedSolutions: string[]
  ): void => {
    const postRequest: PostFormat = {
      url: `/v1/hub/company/challenge/step/${selectedStep}/evaluator/${evaluatorId}/attach/solutions`,
      data: {
        data: assignedSolutions.map((solutionId) => ({
          solutionId: solutionId,
        })),
      },
    };

    assignEvaluator(postRequest, {
      onSuccess: () => {
        sucessfullyAssignEvaluator();
      },
      onError: () => {
        unsucessfullyAssignEvaluator();
      },
    });
  };

  const triggerUpload = (): void => {
    uploadFileRef.current!.click();
  };

  const onSearch = (searchTerm: string): void => {
    setCurrentSearchTerm(searchTerm);
  };

  return (
    <>
      <div className="pb-4">
        <InfoArea areaContent={labels.infoMsg} />
      </div>
      <FilterOrderBar>
        <Filter currentSearchTerm={currentSearchTerm} btnClick={onSearch} />
        {isStepsFetching ? (
          <div className="pt-5">
            <Loading />
          </div>
        ) : (
          <div className="mb-4">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "14px", color: "#495057" }}>Etapa:</span>
              <select
                value={selectedStep}
                onChange={(e) => setSelectedStep(e.target.value)}
                style={{
                  padding: "8px 32px 8px 12px",
                  border: "1px solid #ced4da",
                  borderRadius: "4px",
                  fontSize: "14px",
                  backgroundColor: "white",
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  backgroundImage:
                    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 8px center",
                  minWidth: "180px",
                }}
              >
                {stepsData?.data?.steps?.map((step: any) => (
                  <option key={step.id} value={step.id}>
                    {step.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </FilterOrderBar>
      {isEvaluatorFetching || isSolutionsFetching ? (
        <div className="pt-5">
          <Loading />
        </div>
      ) : (
        <HubCompanyEvaluatorsAssignTable
          evaluators={filteredEvaluatorsData || evaluatorData}
          searchTerm={currentSearchTerm}
          showEditModal={showEditModal}
        />
      )}
    </>
  );
};
